/** VISTOERA_COMPETITIVE_CONTENT_ANALYSIS_V1 — "参考内容分析" tenant page.
 *
 * One persistent two-pane workspace (参考内容列表 + tabbed analysis body),
 * never a right-side drawer (spec §21). Mirrors the backend's Reference/
 * Capture/Analysis layering directly in how state is fetched: the list
 * only ever shows `ReferenceContentSummary` fields (derived status, never
 * a Worker/pipeline stage name — spec §4/§6), and every tab reads from
 * the one `ReferenceContentDetail` payload for the selected id.
 *
 * "采集" is deliberately backgrounded (spec §3): the only user-facing
 * verb here is "开始分析"/"重新分析", never "开始抓取"/"下载视频".
 */
import {
  CheckOutlined,
  DeleteOutlined,
  ExportOutlined,
  FileImageOutlined,
  LoadingOutlined,
  PlusOutlined,
  RedoOutlined,
  SearchOutlined,
} from '@ant-design/icons'
import { Button, Empty, Input, Modal, Pagination, Select, Skeleton, Tabs, Tag, message } from 'antd'
import type { TabsProps } from 'antd'
import type { CSSProperties, MouseEvent, RefObject } from 'react'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import type { TFunction } from 'i18next'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router-dom'
import {
  createReferenceContent,
  deleteReferenceContent,
  detectReferenceContentPlatform,
  getReferenceContentDetail,
  listReferenceContents,
  retryReferenceContentCapture,
  retryReferenceContentFailedStages,
  retryReferenceContentSemanticAnalysis,
  type ReferenceContentDetail,
  type ReferenceContentPlatform,
  type ReferenceContentStatus,
  type ReferenceContentSummary,
  type ReferenceContentTeamBlock,
  type ReferenceContentType,
  type ReferenceLibraryView,
} from '../api'
import { VistoeraActionConfirm } from '../ui/action'
import { formatDateTime } from '../../i18n/dateFormat'
import { useUiLocale } from '../../i18n/LocaleProvider'
import {
  extractReferenceContentUrl,
} from './extractReferenceContentUrl'
import {
  buildHookText,
  buildAiVisualNotes,
  buildObjectiveVisualFacts,
  buildScriptStages,
  formatClockRange,
  isMediaLayerVisible,
  isSafeHostedUrl,
  resolveHostedMediaUrl,
  resolveReferenceCoverUrl,
  resolveReferenceExternalUrl,
  stageStatusGlyph,
  stageVisualState,
  tabStageForDetail,
} from './competitiveContentRealData'
import {
  aggregateStatusDetailI18n,
  aiPendingCopyI18n,
  buildInsightSectionsI18n,
  buildMediaOverviewFactsI18n,
  capturePendingTextI18n,
  contentTypeLabel,
  mediaAnalysisPendingTextI18n,
  platformLabel,
  referenceStatusLabel,
} from './competitiveContentDisplayI18n'
import {
  isRecoveryInFlight,
  isSemanticRetryBlocked,
  recoveryFromDetail,
  shouldShowFailedStageRetry,
  shouldShowFullRerun,
} from './competitiveContentRecovery'
import {
  formatScaleMotion,
  resolveShotTranscriptText,
} from './competitiveContentShotTranscript'
import {
  isTransportSaasError,
  shouldRenderExistingShots,
  shouldRenderExistingTranscript,
} from './competitiveContentTransport'
import { lockPageScroll, unlockPageScroll } from '../utils/scrollLock'
import './competitive-content.css'
import '../ui/tenant-items.css'

const PAGE_SIZE = 20
// Any ReferenceContent in one of these states may still change on its own
// (a future Worker claiming the queued capture Job) — polled lightly so
// the list/detail self-heal without a manual refresh. Terminal states
// (analyzed/failed) never poll.
const LIVE_STATUSES = new Set<ReferenceContentStatus>(['pending', 'analyzing', 'ready', 'partial'])
const POLL_MS = 3000

const PLATFORM_VALUES: ReferenceContentPlatform[] = [
  'douyin',
  'xiaohongshu',
  'weixin_channels',
  'bilibili',
  'youtube',
  'kuaishou',
  'unknown',
]

const CONTENT_TYPE_VALUES: ReferenceContentType[] = ['VIDEO', 'IMAGE_POST', 'MIXED']

const STATUS_VALUES: ReferenceContentStatus[] = ['pending', 'analyzing', 'ready', 'partial', 'analyzed', 'failed']

const STATUS_BADGE_CLASS: Record<ReferenceContentStatus, string> = {
  pending: 'tc-badge-neutral',
  analyzing: 'tc-badge-info',
  ready: 'tc-badge-success',
  partial: 'tc-badge-warning',
  analyzed: 'tc-badge-success',
  failed: 'tc-badge-error',
}

function formatDuration(ms: number | null): string | null {
  if (ms == null) return null
  const totalSeconds = Math.round(ms / 1000)
  const m = Math.floor(totalSeconds / 60)
  const s = totalSeconds % 60
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`
}

function canManageReference(
  item: Pick<ReferenceContentSummary, 'can_manage'>,
  team: ReferenceContentTeamBlock | null,
): boolean {
  if (!team?.enabled) return true
  return item.can_manage === true
}

function referenceCreatorLabel(
  item: ReferenceContentSummary,
  tImageTeam: TFunction<'imageAssets'>,
): string | null {
  if (!item.creator) return null
  return item.creator.is_me ? tImageTeam('team.creator.me') : item.creator.display_name
}

export default function CompetitiveContentAnalysisPage() {
  const { t } = useTranslation(['competitiveAnalysis', 'actions', 'status'])
  const { t: tImageTeam } = useTranslation('imageAssets')
  const { locale } = useUiLocale()
  const { workspaceId = '', id: urlId } = useParams()
  const navigate = useNavigate()

  const platformOptions = useMemo(
    () => PLATFORM_VALUES.map((value) => ({ value, label: platformLabel(value, t) })),
    [t],
  )
  const contentTypeOptions = useMemo(
    () => CONTENT_TYPE_VALUES.map((value) => ({ value, label: contentTypeLabel(value, t) })),
    [t],
  )
  const statusOptions = useMemo(
    () => STATUS_VALUES.map((value) => ({ value, label: referenceStatusLabel(value, t) })),
    [t],
  )

  const [q, setQ] = useState('')
  const [debouncedQ, setDebouncedQ] = useState('')
  const [platform, setPlatform] = useState<ReferenceContentPlatform | undefined>(undefined)
  const [contentType, setContentType] = useState<ReferenceContentType | undefined>(undefined)
  const [status, setStatus] = useState<ReferenceContentStatus | undefined>(undefined)
  const [page, setPage] = useState(1)

  const [items, setItems] = useState<ReferenceContentSummary[]>([])
  const [total, setTotal] = useState(0)
  const [listLoading, setListLoading] = useState(false);
  const [initialLoaded, setInitialLoaded] = useState(false)

  const [selectedId, setSelectedId] = useState<string | null>(urlId ?? null)
  const [detail, setDetail] = useState<ReferenceContentDetail | null>(null)
  const [detailLoading, setDetailLoading] = useState(false)
  const [detailLoadFailed, setDetailLoadFailed] = useState(false)
  const [syncWarning, setSyncWarning] = useState<string | null>(null)
  const [activeTab, setActiveTab] = useState('summary')
  const detailRef = useRef<ReferenceContentDetail | null>(null)
  const selectedIdRef = useRef<string | null>(null)
  const detailRequestGenRef = useRef(0)
  const syncToastAtRef = useRef(0)
  const loadDetailRef = useRef<(opts?: { background?: boolean }) => Promise<void>>(async () => {})
  detailRef.current = detail
  selectedIdRef.current = selectedId

  const [addOpen, setAddOpen] = useState(false)
  const [addUrl, setAddUrl] = useState('')
  const [addPlatform, setAddPlatform] = useState<{
    platform: ReferenceContentPlatform
    platform_label: string
    source_url?: string
  } | null>(null)
  const [creating, setCreating] = useState(false)
  const [retryingSemantic, setRetryingSemantic] = useState(false)
  const [retryingFailedStages, setRetryingFailedStages] = useState(false)

  const [team, setTeam] = useState<ReferenceContentTeamBlock | null>(null)
  const teamEnabled = Boolean(team?.enabled)
  const teamEnabledRef = useRef(false)
  const [libraryView, setLibraryView] = useState<ReferenceLibraryView>('mine')

  useEffect(() => {
    const t = setTimeout(() => {
      setDebouncedQ(q.trim())
      setPage(1)
    }, 300)
    return () => clearTimeout(t)
  }, [q])

  const loadList = useCallback(
    async (opts?: { background?: boolean }) => {
      if (!workspaceId) return
      const background = opts?.background === true
      if (!background) setListLoading(true)
      try {
        const { data } = await listReferenceContents(workspaceId, {
          platform,
          content_type: contentType,
          status,
          q: debouncedQ || undefined,
          page,
          page_size: PAGE_SIZE,
          ...(teamEnabledRef.current ? { view: libraryView } : {}),
        })
        teamEnabledRef.current = Boolean(data.team?.enabled)
        setTeam(data.team ?? null)
        setItems(data.items)
        setTotal(data.total)
      } catch (e) {
        if (background) {
          // Keep last known list; do not toast on every 3s poll miss.
          return
        }
        message.error((e as Error).message || t('competitiveAnalysis:toast.loadListFailed'))
      } finally {
        if (!background) setListLoading(false)
        setInitialLoaded(true)
      }
    },
    [workspaceId, platform, contentType, status, debouncedQ, page, libraryView, t],
  )

  const onChangeLibraryView = (next: ReferenceLibraryView) => {
    if (next === libraryView) return
    setLibraryView(next)
    setPage(1)
  }

  useEffect(() => {
    void loadList()
  }, [loadList])

  useEffect(() => {
    if (!selectedId) return
    if (!items.some((row) => row.id === selectedId)) {
      setSelectedId(items[0]?.id ?? null)
      setDetail(null)
      setDetailLoadFailed(false)
    }
  }, [items, selectedId])

  // Select the first row once the initial (unfiltered) list is in, if the
  // URL didn't already deep-link to one.
  useEffect(() => {
    if (!selectedId && items.length > 0 && page === 1 && !platform && !contentType && !status && !debouncedQ) {
      setSelectedId(items[0].id)
    }
  }, [items, selectedId, page, platform, contentType, status, debouncedQ])

  const loadDetail = useCallback(
    async (opts?: { background?: boolean }) => {
      const background = opts?.background === true
      const requestId = selectedIdRef.current
      if (!workspaceId || !requestId) return

      const gen = ++detailRequestGenRef.current
      if (!background) {
        setDetailLoading(true)
        setDetailLoadFailed(false)
      }

      const maxAttempts = background ? 1 : 3
      const retryDelaysMs = [400, 1000, 2000]
      let lastError: unknown = null

      for (let attempt = 0; attempt < maxAttempts; attempt++) {
        // Superseded by a newer loadDetail call for another selection/refresh.
        if (detailRequestGenRef.current !== gen || selectedIdRef.current !== requestId) return

        try {
          const { data } = await getReferenceContentDetail(workspaceId, requestId)
          if (selectedIdRef.current !== requestId) return
          // If a newer gen started but this record still has no hydrated detail,
          // accept this successful payload (recovers abort/race discards).
          if (detailRequestGenRef.current !== gen && detailRef.current?.id === requestId) return
          setDetail(data)
          setDetailLoadFailed(false)
          setSyncWarning(null)
          if (!background) setDetailLoading(false)
          return
        } catch (e) {
          lastError = e
          if (selectedIdRef.current !== requestId) return
          if (detailRequestGenRef.current !== gen && detailRef.current?.id === requestId) return

          const prior = detailRef.current
          const keepPrior = prior != null && prior.id === requestId
          if (keepPrior || background) {
            setSyncWarning(t('competitiveAnalysis:toast.syncStale'))
            const now = Date.now()
            if (!background && now - syncToastAtRef.current > 8000) {
              syncToastAtRef.current = now
              message.warning(t('competitiveAnalysis:toast.syncStale'))
            }
            if (!background) setDetailLoading(false)
            return
          }

          const canRetry = isTransportSaasError(e) && attempt < maxAttempts - 1
          if (canRetry) {
            await new Promise((resolve) => setTimeout(resolve, retryDelaysMs[attempt] ?? 2000))
            continue
          }
        }
      }

      if (selectedIdRef.current !== requestId) return
      if (detailRef.current?.id === requestId) {
        if (!background) setDetailLoading(false)
        return
      }

      // Initial load exhausted retries — keep selection, show retry panel.
      setDetailLoadFailed(true)
      setSyncWarning(t('competitiveAnalysis:toast.syncStale'))
      if (isTransportSaasError(lastError)) {
        const now = Date.now()
        if (now - syncToastAtRef.current > 8000) {
          syncToastAtRef.current = now
          message.warning(t('competitiveAnalysis:toast.syncStale'))
        }
      } else if (lastError) {
        message.error((lastError as Error).message || t('competitiveAnalysis:toast.loadDetailFailed'))
      }
      if (!background) setDetailLoading(false)
    },
    [workspaceId, t],
  )
  loadDetailRef.current = loadDetail

  // Selection identity only — never re-run because loadDetail/`t` identity churned.
  useEffect(() => {
    setActiveTab('summary')
    setSyncWarning(null)
    setDetailLoadFailed(false)
    const prior = detailRef.current
    if (!selectedId) {
      setDetail(null)
      setDetailLoading(false)
      return
    }
    if (prior && prior.id === selectedId) {
      void loadDetailRef.current({ background: true })
      return
    }
    setDetail(null)
    void loadDetailRef.current({ background: false })
  }, [selectedId])

  useEffect(() => {
    if (selectedId) {
      navigate(`/workspace/${workspaceId}/competitive-content-analysis/${selectedId}`, { replace: true })
    }
  }, [selectedId, workspaceId, navigate])

  // Light self-healing poll — only while something on screen could still
  // change on its own (spec §14/§18: a real Worker may claim a queued
  // capture Job at any time).
  const hasLiveRows = items.some((it) => LIVE_STATUSES.has(it.status)) || (detail && LIVE_STATUSES.has(detail.status))
  useEffect(() => {
    if (!hasLiveRows) return
    const timer = setInterval(() => {
      void loadList({ background: true })
      void loadDetailRef.current({ background: true })
    }, POLL_MS)
    return () => clearInterval(timer)
  }, [hasLiveRows, loadList])

  const detectTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const onAddUrlChange = (value: string) => {
    setAddUrl(value)
    setAddPlatform(null)
    if (detectTimer.current) clearTimeout(detectTimer.current)

    const local = extractReferenceContentUrl(value)
    if (local.ok) {
      // Instant local recognition — populate canonical URL for clarity, but
      // still confirm with the backend detector (same allowlist).
      setAddPlatform({
        platform: local.platform as ReferenceContentPlatform,
        platform_label: local.platformLabel,
        source_url: local.sourceUrl,
      })
    }

    detectTimer.current = setTimeout(async () => {
      const trimmed = value.trim()
      if (!trimmed) return
      try {
        const { data } = await detectReferenceContentPlatform(workspaceId, trimmed)
        setAddPlatform({
          platform: data.platform,
          platform_label: data.platform_label,
          source_url: data.source_url || (local.ok ? local.sourceUrl : undefined),
        })
      } catch {
        // Keep local extract result if backend rejects mid-typing; submit
        // still re-validates server-side.
      }
    }, 400)
  }

  const onSubmitAdd = async () => {
    const trimmed = addUrl.trim()
    if (!trimmed) {
      message.warning(t('competitiveAnalysis:toast.pasteUrlWarning'))
      return
    }
    const local = extractReferenceContentUrl(trimmed)
    if (!local.ok) {
      message.warning(
        local.reason === 'unsupported_platform'
          ? t('competitiveAnalysis:validation.unsupportedPlatform')
          : t('competitiveAnalysis:validation.shareText'),
      )
      return
    }
    setCreating(true)
    try {
      const { data } = await createReferenceContent(workspaceId, local.sourceUrl)
      message.success(t('competitiveAnalysis:toast.analysisStarted'))
      setAddOpen(false)
      setAddUrl('')
      setAddPlatform(null)
      setPage(1)
      setPlatform(undefined)
      setContentType(undefined)
      setStatus(undefined)
      setQ('')
      setSelectedId(data.id)
      await loadList()
    } catch (e) {
      const err = e as { response?: { data?: { detail?: { message?: string } | string } } }
      const detail = err.response?.data?.detail
      const msg =
        typeof detail === 'string'
          ? detail
          : detail && typeof detail === 'object' && 'message' in detail
            ? String((detail as { message?: string }).message || '')
            : ''
      message.error(msg || t('competitiveAnalysis:validation.shareText'))
    } finally {
      setCreating(false)
    }
  }

  const onRetry = async () => {
    if (!selectedId) return
    try {
      await retryReferenceContentCapture(workspaceId, selectedId)
      message.success(t('competitiveAnalysis:toast.retryStarted'))
      await Promise.all([loadDetail(), loadList()])
    } catch (e) {
      message.error((e as Error).message || t('competitiveAnalysis:toast.retryFailed'))
    }
  }

  // Media Layer Contract Reconciliation V1 §5 — a failed media analysis
  // (capture itself succeeded) is retryable without re-downloading the
  // source; kept separate from `onRetry` above (full re-capture).
  const onRetryFailedStages = async () => {
    if (!selectedId || retryingFailedStages) return
    setRetryingFailedStages(true)
    try {
      const { data } = await retryReferenceContentFailedStages(workspaceId, selectedId)
      setDetail(data)
      message.success(
        data.recovery?.active_retry_reused
          ? t('competitiveAnalysis:toast.failedStageRetryAlreadyActive')
          : t('competitiveAnalysis:toast.failedStageRetryStarted'),
      )
      await loadList()
    } catch (e) {
      const err = e as { response?: { data?: { detail?: { message?: string } } }; message?: string }
      message.error(err.response?.data?.detail?.message || err.message || t('competitiveAnalysis:toast.failedStageRetryFailed'))
    } finally {
      setRetryingFailedStages(false)
    }
  }

  const onRetrySemanticAnalysis = async () => {
    if (!selectedId || retryingSemantic) return
    if (isSemanticRetryBlocked(detail)) {
      message.info(t('competitiveAnalysis:toast.failedStageRetryAlreadyActive'))
      return
    }
    setRetryingSemantic(true)
    try {
      const { data } = await retryReferenceContentSemanticAnalysis(workspaceId, selectedId)
      const accepted =
        Boolean((data as { retry_accepted?: boolean; semantic_harness_run_id?: string | null }).retry_accepted) ||
        Boolean((data as { semantic_harness_run_id?: string | null }).semantic_harness_run_id)
      if (!accepted) {
        message.error(t('competitiveAnalysis:toast.aiRetryFailed'))
        return
      }
      if ((data as { active_retry_reused?: boolean }).active_retry_reused) {
        message.info(t('competitiveAnalysis:toast.failedStageRetryAlreadyActive'))
      } else {
        message.success(t('competitiveAnalysis:toast.aiRetryStarted'))
      }
      await Promise.all([loadDetail({ background: false }), loadList()])
    } catch (e) {
      const err = e as { response?: { data?: { detail?: { message?: string } } }; message?: string }
      message.error(err.response?.data?.detail?.message || err.message || t('competitiveAnalysis:toast.aiRetryFailed'))
    } finally {
      setRetryingSemantic(false)
    }
  }

  const deleteItem = async (item: ReferenceContentSummary) => {
    try {
      await deleteReferenceContent(workspaceId, item.id)
      message.success(t('competitiveAnalysis:toast.deleted'))
      if (selectedId === item.id) {
        setSelectedId(null)
        setDetail(null)
        navigate(`/workspace/${workspaceId}/competitive-content-analysis`, { replace: true })
      }
      await loadList()
    } catch (e) {
      const err = e as { response?: { data?: { detail?: { message?: string } } }; message?: string }
      message.error(err.response?.data?.detail?.message || err.message || t('competitiveAnalysis:toast.deleteFailed'))
      throw e
    }
  }

  const onOpenExternal = (item: ReferenceContentSummary, event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation()
    const url = resolveReferenceExternalUrl(item)
    if (!url) return
    window.open(url, '_blank', 'noopener,noreferrer')
  }

  const isImagePost = detail?.content_type === 'IMAGE_POST'
  const isVideoLike = detail?.content_type === 'VIDEO' || detail?.content_type === 'MIXED'
  const showImagePages = detail?.content_type === 'IMAGE_POST' || detail?.content_type === 'MIXED'

  const renderTabLabel = (title: string, tabKey: string) => {
    if (!detail?.analysis_stages?.length) return title
    const state = stageVisualState(tabStageForDetail(detail, tabKey))
    return (
      <span className="cca-tab-label">
        <span>{title}</span>
        <span className={`cca-tab-label-state is-${state}`}>{stageStatusGlyph(state)}</span>
      </span>
    )
  }

  const tabItems: TabsProps['items'] = detail
    ? (
        [
          { key: 'summary', label: renderTabLabel(t('competitiveAnalysis:tabs.summary'), 'summary'), children: <SummaryTab detail={detail} retryingSemantic={retryingSemantic} onRetrySemantic={onRetrySemanticAnalysis} /> },
          isVideoLike || isImagePost
            ? { key: 'transcript', label: renderTabLabel(t('competitiveAnalysis:tabs.transcript'), 'transcript'), children: <TranscriptTab detail={detail} /> }
            : null,
          isVideoLike ? { key: 'shots', label: renderTabLabel(t('competitiveAnalysis:tabs.shots'), 'shots'), children: <ShotTimelineTab detail={detail} /> } : null,
          showImagePages ? { key: 'image_pages', label: t('competitiveAnalysis:tabs.imagePages'), children: <ImagePagesTab detail={detail} /> } : null,
          { key: 'script', label: renderTabLabel(t('competitiveAnalysis:tabs.script'), 'script'), children: <ScriptStructureTab detail={detail} /> },
          { key: 'visual', label: renderTabLabel(t('competitiveAnalysis:tabs.visual'), 'visual'), children: <VisualTab detail={detail} /> },
          { key: 'insights', label: renderTabLabel(t('competitiveAnalysis:tabs.insights'), 'insights'), children: <InsightsTab detail={detail} /> },
        ] as Array<NonNullable<TabsProps['items']>[number] | null>
      ).filter((tab): tab is NonNullable<TabsProps['items']>[number] => tab !== null)
    : []

  return (
    <div className="cca-page">
      <div className="cca-page-header">
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
          <div>
            <div className="cca-page-title">{t('competitiveAnalysis:page.title')}</div>
            <div className="cca-page-subtitle">{t('competitiveAnalysis:page.subtitle')}</div>
          </div>
          <Button type="primary" icon={<PlusOutlined />} onClick={() => setAddOpen(true)}>
            {t('competitiveAnalysis:page.newAnalysis')}
          </Button>
        </div>
      </div>

      {teamEnabled ? (
        <div
          className="iac-view-tabs"
          role="tablist"
          aria-label={tImageTeam('team.viewsAria')}
          data-testid="cca-view-tabs"
        >
          {(['mine', 'shared', 'all'] as const).map((v) => (
            <button
              key={v}
              type="button"
              role="tab"
              aria-selected={libraryView === v}
              className={`iac-view-tab${libraryView === v ? ' is-active' : ''}`}
              onClick={() => onChangeLibraryView(v)}
              data-testid={`cca-view-tab-${v}`}
            >
              {tImageTeam(`team.views.${v}`)}
            </button>
          ))}
        </div>
      ) : null}

      <div className="cca-workspace">
        <div className="cca-list-panel">
          <div className="cca-list-header">
            <div className="cca-list-header-row">
              <span className="cca-list-title">{t('competitiveAnalysis:list.title')}</span>
              <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>{t('competitiveAnalysis:list.total', { count: total })}</span>
            </div>
            <Input
              size="small"
              placeholder={t('competitiveAnalysis:list.searchPlaceholder')}
              prefix={<SearchOutlined />}
              value={q}
              onChange={(e) => setQ(e.target.value)}
              allowClear
            />
            <div className="cca-list-filters">
              <Select
                size="small"
                allowClear
                placeholder={t('competitiveAnalysis:filters.allPlatforms')}
                value={platform}
                onChange={(v) => {
                  setPlatform(v)
                  setPage(1)
                }}
                options={platformOptions}
              />
              <Select
                size="small"
                allowClear
                placeholder={t('competitiveAnalysis:filters.allTypes')}
                value={contentType}
                onChange={(v) => {
                  setContentType(v)
                  setPage(1)
                }}
                options={contentTypeOptions}
              />
              <Select
                size="small"
                allowClear
                placeholder={t('competitiveAnalysis:filters.status')}
                value={status}
                onChange={(v) => {
                  setStatus(v)
                  setPage(1)
                }}
                options={statusOptions}
              />
            </div>
          </div>

          <div className="cca-list-scroll">
            {listLoading && !initialLoaded ? (
              <Skeleton active paragraph={{ rows: 6 }} style={{ padding: 12 }} />
            ) : items.length === 0 ? (
              <Empty
                style={{ marginTop: 60 }}
                description={
                  total === 0 && !debouncedQ && !platform && !contentType && !status
                    ? t('competitiveAnalysis:empty.listNone')
                    : t('competitiveAnalysis:empty.listNoMatch')
                }
              />
            ) : (
              items.map((item) => {
                const externalUrl = resolveReferenceExternalUrl(item)
                const coverUrl = resolveReferenceCoverUrl(item)
                const creatorLabel = referenceCreatorLabel(item, tImageTeam)
                return (
                <div
                  key={item.id}
                  className={`cca-list-item${item.id === selectedId ? ' is-active' : ''}`}
                  data-testid={`cca-list-item-${item.id}`}
                  onClick={() => {
                    if (item.id === selectedId) {
                      // Re-click active row: soft background sync (keeps last-known detail).
                      void loadDetail({ background: true })
                      return
                    }
                    setSelectedId(item.id)
                  }}
                >
                  <div className="cca-list-thumb">
                    <ReferenceCoverThumb coverUrl={coverUrl} />
                  </div>
                  <div className="cca-list-item-body">
                    <div className="cca-list-item-title-row">
                      <div className="cca-list-item-title">{item.title || t('competitiveAnalysis:list.noTitle')}</div>
                      <div className="cca-list-item-actions">
                        <button
                          type="button"
                          className="cca-list-item-action"
                          aria-label={t('competitiveAnalysis:list.viewOriginal')}
                          title={externalUrl ? t('competitiveAnalysis:list.viewOriginal') : t('competitiveAnalysis:list.originalUnavailable')}
                          disabled={!externalUrl}
                          onClick={(e) => onOpenExternal(item, e)}
                        >
                          <ExportOutlined />
                        </button>
                        {canManageReference(item, team) ? (
                          <VistoeraActionConfirm
                            title={t('competitiveAnalysis:deleteConfirm.title')}
                            description={
                              <>
                                <p style={{ margin: 0 }}>{t('competitiveAnalysis:deleteConfirm.body')}</p>
                                {item.status === 'analyzed' || item.status === 'ready' || item.status === 'partial' ? (
                                  <p style={{ margin: '6px 0 0' }}>{t('competitiveAnalysis:deleteConfirm.snapshotWarning')}</p>
                                ) : null}
                                <p style={{ margin: '6px 0 0' }}>
                                  <strong>{item.title || t('competitiveAnalysis:list.noTitle')}</strong>
                                </p>
                              </>
                            }
                            confirmLabel={t('actions:delete')}
                            tooltip={t('actions:delete')}
                            onConfirm={() => deleteItem(item)}
                          >
                            <button
                              type="button"
                              className="cca-list-item-action cca-list-item-action-danger"
                              aria-label={t('actions:delete')}
                            >
                              <DeleteOutlined />
                            </button>
                          </VistoeraActionConfirm>
                        ) : null}
                      </div>
                    </div>
                    <div className="cca-list-item-meta">
                      <span className="cca-list-item-meta-text">
                        {item.platform_label}
                        {item.author_name ? ` · ${item.author_name}` : ''}
                        {teamEnabled && creatorLabel ? ` · ${creatorLabel}` : ''}
                        {teamEnabled && item.visibility === 'workspace' ? (
                          <Tag bordered={false} color="blue" style={{ marginInlineStart: 6, fontSize: 10, lineHeight: '16px' }}>
                            {tImageTeam('team.share.shared')}
                          </Tag>
                        ) : null}
                      </span>
                    </div>
                    <div className="cca-list-item-footer">
                      <span style={{ fontSize: 11.5, color: 'var(--text-muted)' }}>
                        {item.duration_ms != null ? formatDuration(item.duration_ms) : formatDateTime(item.created_at, locale).slice(0, 16)}
                      </span>
                      <span className={`tc-badge ${STATUS_BADGE_CLASS[item.status]}`}>{referenceStatusLabel(item.status, t)}</span>
                    </div>
                  </div>
                </div>
              )})
            )}
          </div>

          {total > PAGE_SIZE && (
            <div className="cca-list-pagination">
              <Pagination
                size="small"
                simple
                current={page}
                pageSize={PAGE_SIZE}
                total={total}
                onChange={(p) => setPage(p)}
              />
            </div>
          )}
        </div>

        <div className="cca-detail-panel">
          {!selectedId ? (
            <div className="cca-detail-empty">
              <Empty description={t('competitiveAnalysis:empty.detailSelect')} />
            </div>
          ) : detailLoading && !detail ? (
            <div style={{ padding: 24 }}>
              <Skeleton active paragraph={{ rows: 8 }} />
            </div>
          ) : detail ? (
            <>
              <div className="cca-detail-header">
                <div className="cca-detail-cover">
                  <ReferenceCoverThumb
                    coverUrl={resolveReferenceCoverUrl(detail, detail)}
                    iconStyle={{ fontSize: 22, color: 'var(--text-muted)' }}
                  />
                </div>
                <div className="cca-detail-head-main">
                  <div className="cca-detail-title-row">
                    <div className="cca-detail-title">{detail.title || t('competitiveAnalysis:list.noTitle')}</div>
                    <div className="cca-detail-actions">
                      {shouldShowFailedStageRetry(detail) ? (
                        <Button
                          size="small"
                          type="primary"
                          icon={<RedoOutlined />}
                          loading={retryingFailedStages}
                          onClick={onRetryFailedStages}
                          data-testid="cca-retry-failed-stages"
                        >
                          {retryingFailedStages
                            ? t('competitiveAnalysis:detail.retryingFailedStages')
                            : t('competitiveAnalysis:detail.retryFailedStages')}
                        </Button>
                      ) : null}
                      {shouldShowFullRerun(detail) ? (
                        <Button size="small" icon={<RedoOutlined />} onClick={onRetry}>
                          {t('competitiveAnalysis:detail.fullRerun')}
                        </Button>
                      ) : null}
                      {(detail.analysis?.status === 'failed' || (detail.status === 'partial' && detail.analysis_progress?.ai_failed)) &&
                      detail.media_analysis &&
                      (detail.media_analysis.status === 'READY' || detail.media_analysis.status === 'PARTIAL') &&
                      !shouldShowFailedStageRetry(detail) ? (
                        <Button
                          size="small"
                          icon={<RedoOutlined />}
                          loading={retryingSemantic}
                          disabled={retryingSemantic || isSemanticRetryBlocked(detail)}
                          onClick={onRetrySemanticAnalysis}
                        >
                          {retryingSemantic ? t('competitiveAnalysis:detail.retryingAiAnalysis') : t('competitiveAnalysis:detail.retryAiAnalysis')}
                        </Button>
                      ) : null}
                      {(() => {
                        const externalUrl = resolveReferenceExternalUrl(detail)
                        if (!externalUrl) {
                          return (
                            <Button size="small" disabled>
                              {t('competitiveAnalysis:list.originalUnavailable')}
                            </Button>
                          )
                        }
                        return (
                          <Button
                            size="small"
                            icon={<ExportOutlined />}
                            href={externalUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            {t('competitiveAnalysis:detail.viewOriginal')}
                          </Button>
                        )
                      })()}
                    </div>
                  </div>
                  <div className="cca-detail-meta-row">
                    <Tag>{detail.platform_label}</Tag>
                    {detail.author_name && <span>{detail.author_name}</span>}
                    {detail.published_at && <span>{formatDateTime(detail.published_at, locale)}</span>}
                    {detail.duration_ms != null && <span>{formatDuration(detail.duration_ms)}</span>}
                    <span className={`tc-badge ${STATUS_BADGE_CLASS[detail.status]}`}>
                      {detail.status === 'analyzing' && <LoadingOutlined style={{ marginRight: 4 }} />}
                      {referenceStatusLabel(detail.status, t)}
                    </span>
                  </div>
                  <FailedStageRecoveryBanner
                    detail={detail}
                    retrying={retryingFailedStages}
                    onRetry={onRetryFailedStages}
                    t={t}
                  />
                  {syncWarning ? (
                    <div className="cca-detail-sync-warning" role="status">
                      {syncWarning}
                    </div>
                  ) : null}
                </div>
              </div>

              <div className="cca-detail-tabs">
                <Tabs activeKey={activeTab} onChange={setActiveTab} items={tabItems} />
              </div>
            </>
          ) : (
            <div className="cca-detail-empty cca-detail-load-failed">
              <Empty
                description={
                  detailLoadFailed || syncWarning
                    ? t('competitiveAnalysis:toast.syncStale')
                    : t('competitiveAnalysis:toast.loadDetailFailed')
                }
              />
              <Button
                type="primary"
                onClick={() => {
                  setDetailLoadFailed(false)
                  void loadDetail({ background: false })
                }}
              >
                {t('competitiveAnalysis:detail.retryLoad')}
              </Button>
            </div>
          )}
        </div>
      </div>

      <Modal
        title={t('competitiveAnalysis:modal.addTitle')}
        open={addOpen}
        onCancel={() => setAddOpen(false)}
        onOk={onSubmitAdd}
        okText={t('competitiveAnalysis:modal.startAnalysis')}
        okButtonProps={{ loading: creating }}
        destroyOnHidden
      >
        <Input.TextArea
          placeholder={t('competitiveAnalysis:modal.urlPlaceholder')}
          value={addUrl}
          onChange={(e) => onAddUrlChange(e.target.value)}
          onPressEnter={(e) => {
            if (!e.shiftKey) {
              e.preventDefault()
              void onSubmitAdd()
            }
          }}
          autoFocus
          autoSize={{ minRows: 3, maxRows: 6 }}
        />
        <div className="cca-add-modal-platform-row">
          {addPlatform ? (
            <>
              {t('competitiveAnalysis:modal.detected')}
              <Tag color="purple">{addPlatform.platform_label}</Tag>
              {addPlatform.source_url ? <span className="cca-add-modal-extracted-url">{addPlatform.source_url}</span> : null}
            </>
          ) : null}
        </div>
      </Modal>
    </div>
  )
}

// ===================================================================
// Tab renderers
// ===================================================================

function PendingBanner({ text }: { text: string }) {
  return (
    <div className="cca-tab-pending">
      <LoadingOutlined />
      {text}
    </div>
  )
}

function FailedStageRecoveryBanner({
  detail,
  retrying,
  onRetry,
  t,
}: {
  detail: ReferenceContentDetail
  retrying: boolean
  onRetry: () => void
  t: TFunction
}) {
  const recovery = recoveryFromDetail(detail)
  const inFlight = isRecoveryInFlight(detail)
  const statusDetail = aggregateStatusDetailI18n(detail, t)

  if (inFlight) {
    const kind = recovery?.recovery_kind
    const executionState = recovery?.execution_state
    let retryingLabel = t('competitiveAnalysis:recovery.retryingGeneric')
    if (kind === 'capture') {
      retryingLabel = t('competitiveAnalysis:recovery.retryingCapture')
    } else if (kind === 'media') {
      retryingLabel = t('competitiveAnalysis:recovery.retryingMedia')
    } else if (kind === 'semantic') {
      retryingLabel =
        executionState === 'QUEUED'
          ? t('competitiveAnalysis:recovery.queuedAi')
          : t('competitiveAnalysis:recovery.runningAi')
    }
    return (
      <div
        className={`cca-detail-recovery-banner is-retrying${executionState === 'QUEUED' ? ' is-queued' : ''}`}
        data-execution-state={executionState || undefined}
      >
        <LoadingOutlined />
        <span>{retryingLabel}</span>
      </div>
    )
  }

  if (!statusDetail || (detail.status !== 'failed' && detail.status !== 'partial')) return null

  const showRetry = shouldShowFailedStageRetry(detail)
  const configAction =
    recovery?.recommended_action === 'CONFIGURE_CREDENTIALS'
      ? t('competitiveAnalysis:recovery.configureCredentials')
      : null

  return (
    <div className={`cca-detail-recovery-banner${detail.status === 'partial' ? ' is-warning' : ' is-error'}`}>
      <div className="cca-detail-recovery-copy">
        <div className="cca-detail-recovery-title">{statusDetail}</div>
        {recovery?.diagnostic_code && recovery.retryable === false ? (
          <div className="cca-detail-recovery-hint">
            {t('competitiveAnalysis:recovery.nonRetryableHint', { code: recovery.diagnostic_code })}
          </div>
        ) : null}
      </div>
      <div className="cca-detail-recovery-actions">
        {showRetry ? (
          <Button
            size="small"
            type="primary"
            icon={<RedoOutlined />}
            loading={retrying}
            onClick={onRetry}
            data-testid="cca-retry-failed-stages-inline"
          >
            {retrying ? t('competitiveAnalysis:detail.retryingFailedStages') : t('competitiveAnalysis:detail.retryFailedStages')}
          </Button>
        ) : null}
        {configAction ? (
          <Button size="small" disabled>
            {configAction}
          </Button>
        ) : null}
      </div>
    </div>
  )
}

// Media Layer Contract Reconciliation V1 §16 — "Media Analysis READY、AI
// Analysis 尚未 READY 时：用户应该已经可以看到转写文本/镜头时间轴/关键帧".
function mediaAnalysisPendingText(detail: ReferenceContentDetail, t: ReturnType<typeof useTranslation>['t']): string | null {
  return mediaAnalysisPendingTextI18n(detail, t)
}

const MODAL_SURFACE_CLASS = 'vst-modal-surface'

function KeyframeLightbox({ url, onClose }: { url: string; onClose: () => void }) {
  useEffect(() => {
    lockPageScroll()
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', onKey)
    return () => {
      window.removeEventListener('keydown', onKey)
      unlockPageScroll()
    }
  }, [onClose])

  return (
    <div className={`cca-lightbox ${MODAL_SURFACE_CLASS}`} role="dialog" aria-modal="true" onClick={onClose}>
      <img src={url} alt="" className="cca-lightbox-img" onClick={(e) => e.stopPropagation()} />
    </div>
  )
}

function StageProgressSummary({ detail }: { detail: ReferenceContentDetail }) {
  const { t } = useTranslation(['competitiveAnalysis'])
  const stages = detail.analysis_stages || []
  if (!stages.length) return null
  const progress = detail.analysis_progress
  return (
    <div className="cca-stage-progress">
      <div className="cca-tab-section-title">{t('competitiveAnalysis:summary.progressTitle')}</div>
      {progress ? (
        <div className="cca-stage-progress-compact">
          {t('competitiveAnalysis:summary.basicProgress', { completed: progress.media_completed, total: progress.media_total })}
          <span className="cca-stage-progress-sep">·</span>
          {t('competitiveAnalysis:summary.aiProgress', { completed: progress.ai_completed, total: progress.ai_total })}
        </div>
      ) : null}
      <div className="cca-stage-progress-list">
        {stages.map((stage) => {
          const state = stageVisualState(stage)
          return (
            <div className="cca-stage-progress-item" key={stage.id}>
              <span className={`cca-stage-progress-glyph is-${state}`}>{stageStatusGlyph(state)}</span>
              <span className="cca-stage-progress-label">{stage.label}</span>
              {state === 'running' ? <LoadingOutlined className="cca-stage-progress-spinner" /> : null}
            </div>
          )
        })}
      </div>
    </div>
  )
}

function SummaryTab({
  detail,
  retryingSemantic,
  onRetrySemantic,
}: {
  detail: ReferenceContentDetail
  retryingSemantic: boolean
  onRetrySemantic: () => void
}) {
  const { t } = useTranslation(['competitiveAnalysis', 'actions'])
  const analysis = detail.analysis
  const media = detail.media_analysis
  const capture = detail.capture
  const facts = buildMediaOverviewFactsI18n(detail, t)
  const mediaVisible = isMediaLayerVisible(media)
  const aiReady = analysis?.status === 'succeeded'

  if ((!capture || capture.status === 'pending' || capture.status === 'running') && detail.status === 'pending') {
    return (
      <div className="cca-tab-body">
        <PendingBanner text={t('competitiveAnalysis:pending.waitingAnalysis')} />
      </div>
    )
  }

  if (capture && (capture.status === 'pending' || capture.status === 'running') && !mediaVisible) {
    return (
      <div className="cca-tab-body">
        <PendingBanner text={t('competitiveAnalysis:pending.fetchingContent')} />
      </div>
    )
  }

  if (capture?.status === 'failed' && !mediaVisible) {
    return (
      <div className="cca-tab-body">
        <div className="cca-tab-failed">{detail.status_detail || t('competitiveAnalysis:pending.captureFailed')}</div>
      </div>
    )
  }

  return (
    <div className="cca-tab-body">
      <StageProgressSummary detail={detail} />
      <div className="cca-tab-section">
        <div className="cca-tab-section-title">{t('competitiveAnalysis:summary.mediaOverview')}</div>
        {!media || media.status === 'PENDING' || media.status === 'RUNNING' ? (
          <PendingBanner text={t('competitiveAnalysis:pending.analyzing')} />
        ) : media.status === 'FAILED' ? (
          <div className="cca-tab-failed">{media.error_message || t('competitiveAnalysis:summary.analysisFailed')}</div>
        ) : (
          <div className="cca-overview-grid">
            <div className="cca-overview-item">
              <div className="cca-overview-label">{t('competitiveAnalysis:overview.duration')}</div>
              <div className="cca-overview-value">{facts.durationLabel || '—'}</div>
            </div>
            <div className="cca-overview-item">
              <div className="cca-overview-label">{t('competitiveAnalysis:overview.dimensions')}</div>
              <div className="cca-overview-value">{facts.dimensionsLabel || '—'}</div>
            </div>
            <div className="cca-overview-item">
              <div className="cca-overview-label">{t('competitiveAnalysis:overview.contentType')}</div>
              <div className="cca-overview-value">{facts.contentTypeLabel || '—'}</div>
            </div>
            <div className="cca-overview-item">
              <div className="cca-overview-label">{t('competitiveAnalysis:overview.transcriptStatus')}</div>
              <div className="cca-overview-value">{facts.transcriptStatusLabel || '—'}</div>
            </div>
            <div className="cca-overview-item">
              <div className="cca-overview-label">{t('competitiveAnalysis:overview.shotCount')}</div>
              <div className="cca-overview-value">{facts.shotCount}</div>
            </div>
            <div className="cca-overview-item">
              <div className="cca-overview-label">{t('competitiveAnalysis:overview.keyframeCount')}</div>
              <div className="cca-overview-value">{facts.keyframeCount}</div>
            </div>
            <div className="cca-overview-item">
              <div className="cca-overview-label">{t('competitiveAnalysis:overview.mediaAnalysis')}</div>
              <div className="cca-overview-value">{facts.mediaStatusLabel || '—'}</div>
            </div>
          </div>
        )}
      </div>

      {mediaVisible && !aiReady ? (
        <div className="cca-tab-section">
          <div className="cca-tab-section-title">{t('competitiveAnalysis:summary.aiDeepAnalysis')}</div>
          {analysis?.status === 'failed' && !isRecoveryInFlight(detail) ? (
            <div className="cca-tab-failed">
              <div>{analysis.error_message || t('competitiveAnalysis:summary.aiFailedDefault')}</div>
              <Button
                size="small"
                icon={<RedoOutlined />}
                loading={retryingSemantic}
                disabled={retryingSemantic || isSemanticRetryBlocked(detail)}
                onClick={onRetrySemantic}
                style={{ marginTop: 8 }}
              >
                {retryingSemantic ? t('competitiveAnalysis:detail.retryingAiAnalysis') : t('competitiveAnalysis:detail.retryAiAnalysis')}
              </Button>
            </div>
          ) : (
            <PendingBanner
              text={
                retryingSemantic || isRecoveryInFlight(detail)
                  ? t('competitiveAnalysis:detail.retryingAiAnalysis')
                  : aiPendingCopyI18n(detail, t)
              }
            />
          )}
        </div>
      ) : null}

      {aiReady && analysis?.summary ? (
        <div className="cca-tab-section">
          <div className="cca-tab-section-title">{t('competitiveAnalysis:summary.contentSummary')}</div>
          <div className="cca-summary-text">{analysis.summary}</div>
        </div>
      ) : null}
      {aiReady && analysis?.highlights && analysis.highlights.length > 0 ? (
        <div className="cca-tab-section">
          <div className="cca-tab-section-title">{t('competitiveAnalysis:summary.highlights')}</div>
          <div className="cca-highlight-list">
            {analysis.highlights.map((h, i) => (
              <div className="cca-highlight-item" key={i}>
                <CheckOutlined />
                <span>{h}</span>
              </div>
            ))}
          </div>
        </div>
      ) : null}
      {aiReady && analysis?.tags && analysis.tags.length > 0 ? (
        <div className="cca-tab-section">
          <div className="cca-tab-section-title">{t('competitiveAnalysis:summary.tags')}</div>
          <div className="cca-tag-list">
            {analysis.tags.map((t) => (
              <Tag key={t}>{t}</Tag>
            ))}
          </div>
        </div>
      ) : null}
    </div>
  )
}

function TranscriptTab({ detail }: { detail: ReferenceContentDetail }) {
  const { t } = useTranslation(['competitiveAnalysis'])
  const hasTranscript = shouldRenderExistingTranscript(detail)
  const capturePending = capturePendingTextI18n(detail, t)
  if (capturePending && !hasTranscript) {
    return (
      <div className="cca-tab-body">
        <PendingBanner text={capturePending} />
      </div>
    )
  }
  const mediaPending = mediaAnalysisPendingText(detail, t)
  if (mediaPending && !hasTranscript) {
    return (
      <div className="cca-tab-body">
        <PendingBanner text={mediaPending} />
      </div>
    )
  }
  if (!hasTranscript) {
    const asrStatus = (detail.media_analysis?.component_status?.asr || '').toUpperCase()
    const emptyDescription =
      asrStatus === 'SUCCEEDED'
        ? t('competitiveAnalysis:transcript.noSpeech')
        : t('competitiveAnalysis:transcript.empty')
    return (
      <div className="cca-tab-body">
        <Empty description={emptyDescription} />
      </div>
    )
  }

  const onExport = () => {
    const text = detail.transcript.map((seg) => `${formatClockRange(seg.start_ms, seg.end_ms)}\n${seg.text}`).join('\n\n')
    navigator.clipboard?.writeText(text).then(
      () => message.success(t('competitiveAnalysis:toast.transcriptCopied')),
      () => message.error(t('competitiveAnalysis:toast.copyFailed')),
    )
  }

  return (
    <div className="cca-tab-body">
      {mediaPending ? <PendingBanner text={mediaPending} /> : null}
      <div className="cca-transcript-toolbar">
        <Button size="small" onClick={onExport}>
          {t('competitiveAnalysis:transcript.export')}
        </Button>
      </div>
      <div className="cca-transcript-list">
        {detail.transcript.map((seg) => (
          <div className="cca-transcript-item" key={seg.index}>
            <span className="cca-transcript-time">{formatClockRange(seg.start_ms, seg.end_ms)}</span>
            <span className="cca-transcript-text">{seg.text}</span>
          </div>
        ))}
      </div>
    </div>
  )
}

function ShotKeyframeImage({ url }: { url: string }) {
  const { t } = useTranslation(['competitiveAnalysis'])
  const [failed, setFailed] = useState(false)
  if (failed) {
    return <span className="cca-frame-placeholder">{t('competitiveAnalysis:shots.previewUnavailable')}</span>
  }
  return <img src={url} alt="" loading="lazy" decoding="async" onError={() => setFailed(true)} />
}

type HoverPreviewState = {
  url: string
  shotIndex: number
  anchor: DOMRect
}

function computeHoverPreviewStyle(anchor: DOMRect): CSSProperties {
  const gap = 12
  const maxW = Math.min(560, window.innerWidth - 24)
  const maxH = Math.min(window.innerHeight * 0.65, window.innerHeight - 24)
  let left = anchor.right + gap
  if (left + maxW > window.innerWidth - 12) {
    left = Math.max(12, anchor.left - gap - maxW)
  }
  let top = anchor.top
  if (top + maxH > window.innerHeight - 12) {
    top = Math.max(12, window.innerHeight - 12 - maxH)
  }
  return {
    position: 'fixed',
    left,
    top,
    maxWidth: maxW,
    maxHeight: maxH,
    zIndex: 1100,
  }
}

function ShotKeyframeHoverPreview({ state }: { state: HoverPreviewState | null }) {
  const { t } = useTranslation(['competitiveAnalysis'])
  if (!state) return null
  return createPortal(
    <div
      className="cca-shot-hover-preview"
      style={computeHoverPreviewStyle(state.anchor)}
      role="img"
      aria-label={t('competitiveAnalysis:shots.hoverPreview', { index: state.shotIndex + 1 })}
    >
      <img src={state.url} alt="" />
    </div>,
    document.body,
  )
}

function useForwardVerticalWheelToAncestor(ref: RefObject<HTMLElement | null>, ancestorSelector: string) {
  useEffect(() => {
    const el = ref.current
    if (!el) return
    const onWheel = (e: WheelEvent) => {
      if (e.shiftKey || Math.abs(e.deltaX) > Math.abs(e.deltaY)) return
      const scrollParent = el.closest(ancestorSelector) as HTMLElement | null
      if (!scrollParent || scrollParent.scrollHeight <= scrollParent.clientHeight + 1) return
      scrollParent.scrollTop += e.deltaY
      e.preventDefault()
    }
    el.addEventListener('wheel', onWheel, { passive: false })
    return () => el.removeEventListener('wheel', onWheel)
  }, [ref, ancestorSelector])
}

function ShotTimelineTab({ detail }: { detail: ReferenceContentDetail }) {
  const { t } = useTranslation(['competitiveAnalysis'])
  const [hoverPreview, setHoverPreview] = useState<HoverPreviewState | null>(null)
  const hoverTimerRef = useRef<number | null>(null)
  const shotTableRef = useRef<HTMLDivElement>(null)
  useForwardVerticalWheelToAncestor(shotTableRef, '.ant-tabs-body-holder, .ant-tabs-content-holder')

  const clearHoverTimer = () => {
    if (hoverTimerRef.current != null) {
      window.clearTimeout(hoverTimerRef.current)
      hoverTimerRef.current = null
    }
  }

  useEffect(() => () => clearHoverTimer(), [])

  const capturePending = capturePendingTextI18n(detail, t)
  const hasShots = shouldRenderExistingShots(detail)
  if (capturePending && !hasShots) {
    return (
      <div className="cca-tab-body">
        <PendingBanner text={capturePending} />
      </div>
    )
  }
  const mediaPending = mediaAnalysisPendingText(detail, t)
  if (mediaPending && !hasShots) {
    return (
      <div className="cca-tab-body">
        <PendingBanner text={mediaPending} />
      </div>
    )
  }
  if (!hasShots) {
    return (
      <div className="cca-tab-body">
        <Empty description={t('competitiveAnalysis:shots.empty')} />
      </div>
    )
  }

  const showHover = (shotIndex: number, url: string, el: HTMLElement) => {
    clearHoverTimer()
    hoverTimerRef.current = window.setTimeout(() => {
      setHoverPreview({ url, shotIndex, anchor: el.getBoundingClientRect() })
    }, 60)
  }

  const hideHover = () => {
    clearHoverTimer()
    setHoverPreview(null)
  }

  return (
    <div className="cca-tab-body">
      {mediaPending ? <PendingBanner text={mediaPending} /> : null}
      <div className="cca-shot-table-wrap" ref={shotTableRef}>
        <table className="cca-shot-table">
          <thead>
            <tr>
              <th className="cca-shot-col-shot">{t('competitiveAnalysis:shots.columns.shot')}</th>
              <th className="cca-shot-col-frame">{t('competitiveAnalysis:shots.columns.frame')}</th>
              <th className="cca-shot-col-time">{t('competitiveAnalysis:shots.columns.time')}</th>
              <th className="cca-shot-col-duration">{t('competitiveAnalysis:shots.columns.duration')}</th>
              <th className="cca-shot-col-copy">{t('competitiveAnalysis:shots.columns.copy')}</th>
              <th className="cca-shot-col-scale">{t('competitiveAnalysis:shots.columns.scaleMotion')}</th>
              <th className="cca-shot-col-visual">{t('competitiveAnalysis:shots.columns.visual')}</th>
              <th className="cca-shot-col-role">{t('competitiveAnalysis:shots.columns.function')}</th>
            </tr>
          </thead>
          <tbody>
            {detail.shots.map((shot) => {
              const url = resolveHostedMediaUrl(shot.representative_frame_url)
              const copy = resolveShotTranscriptText(shot, detail.transcript)
              return (
                <tr key={shot.index}>
                  <td className="cca-shot-col-shot">{String(shot.index + 1).padStart(2, '0')}</td>
                  <td className="cca-shot-col-frame">
                    <div
                      className="cca-shot-row-thumb"
                      onPointerEnter={(e) => {
                        if (!url) return
                        showHover(shot.index, url, e.currentTarget)
                      }}
                      onPointerLeave={hideHover}
                      onFocus={(e) => {
                        if (!url) return
                        showHover(shot.index, url, e.currentTarget)
                      }}
                      onBlur={hideHover}
                      tabIndex={url ? 0 : -1}
                      role="img"
                      aria-label={
                        url
                          ? t('competitiveAnalysis:shots.viewKeyframe', { index: shot.index + 1 })
                          : t('competitiveAnalysis:shots.noKeyframe')
                      }
                    >
                      {url ? (
                        <ShotKeyframeImage url={url} />
                      ) : (
                        <span className="cca-frame-placeholder">{t('competitiveAnalysis:shots.noKeyframe')}</span>
                      )}
                    </div>
                  </td>
                  <td className="cca-shot-col-time">{formatClockRange(shot.start_ms, shot.end_ms)}</td>
                  <td className="cca-shot-col-duration">
                    {Math.max(1, Math.round((shot.end_ms - shot.start_ms) / 1000))}s
                  </td>
                  <td className="cca-shot-col-copy">
                    {copy ? (
                      <span className="cca-shot-copy-text" title={copy}>
                        {copy}
                      </span>
                    ) : (
                      '—'
                    )}
                  </td>
                  <td className="cca-shot-col-scale">{formatScaleMotion(shot.shot_scale, shot.camera_motion)}</td>
                  <td className="cca-shot-col-visual">
                    {shot.visual_description ? (
                      <span className="cca-shot-visual-text" title={shot.visual_description}>
                        {shot.visual_description}
                      </span>
                    ) : (
                      '—'
                    )}
                  </td>
                  <td className="cca-shot-col-role">{shot.narrative_function || '—'}</td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
      <ShotKeyframeHoverPreview state={hoverPreview} />
    </div>
  )
}

function ImagePagesTab({ detail }: { detail: ReferenceContentDetail }) {
  const { t } = useTranslation(['competitiveAnalysis'])
  const [lightboxUrl, setLightboxUrl] = useState<string | null>(null)

  if (!detail.capture || detail.capture.status !== 'succeeded') {
    const capturePending = capturePendingTextI18n(detail, t)
    return (
      <div className="cca-tab-body">
        <PendingBanner text={capturePending || t('competitiveAnalysis:pending.imagePagesFetching')} />
      </div>
    )
  }
  const mediaPending = mediaAnalysisPendingText(detail, t)
  if (mediaPending) {
    return (
      <div className="cca-tab-body">
        <PendingBanner text={mediaPending} />
      </div>
    )
  }
  if (detail.image_pages.length === 0) {
    return (
      <div className="cca-tab-body">
        <Empty description={t('competitiveAnalysis:imagePages.empty')} />
      </div>
    )
  }

  return (
    <div className="cca-tab-body">
      <div className="cca-image-page-grid">
        {detail.image_pages.map((page) => {
          const url = isSafeHostedUrl(page.image_url) ? page.image_url : null
          return (
            <div className="cca-image-page-card" key={page.index}>
              <button type="button" className="cca-image-page-thumb" onClick={() => url && setLightboxUrl(url)} disabled={!url}>
                <span className="cca-image-page-index">{t('competitiveAnalysis:imagePages.index', { index: page.index + 1 })}</span>
                {url ? <img src={url} alt="" /> : <span className="cca-frame-placeholder">{t('competitiveAnalysis:imagePages.noImage')}</span>}
              </button>
              {page.page_role && <div className="cca-image-page-role">{page.page_role}</div>}
              {page.caption_text && <div className="cca-image-page-caption">{page.caption_text}</div>}
            </div>
          )
        })}
      </div>
      {lightboxUrl ? <KeyframeLightbox url={lightboxUrl} onClose={() => setLightboxUrl(null)} /> : null}
    </div>
  )
}

function ScriptStructureTab({ detail }: { detail: ReferenceContentDetail }) {
  const { t } = useTranslation(['competitiveAnalysis'])
  const analysis = detail.analysis
  const capturePending = capturePendingTextI18n(detail, t)
  if (capturePending) {
    return (
      <div className="cca-tab-body">
        <PendingBanner text={capturePending} />
      </div>
    )
  }
  if (!analysis || analysis.status !== 'succeeded') {
    return (
      <div className="cca-tab-body">
        {analysis?.status === 'failed' ? (
          <div className="cca-tab-failed">{analysis.error_message || t('competitiveAnalysis:summary.aiFailedDefault')}</div>
        ) : (
          <PendingBanner text={aiPendingCopyI18n(detail, t)} />
        )}
      </div>
    )
  }

  const stages = buildScriptStages(analysis)
  const hookText = buildHookText(analysis)

  if (!hookText && stages.length === 0) {
    return (
      <div className="cca-tab-body">
        <Empty description={t('competitiveAnalysis:script.empty')} />
      </div>
    )
  }

  return (
    <div className="cca-tab-body">
      {hookText && (
        <div className="cca-tab-section">
          <div className="cca-tab-section-title">{t('competitiveAnalysis:script.hookTitle')}</div>
          <div className="cca-summary-text" style={{ whiteSpace: 'pre-wrap' }}>
            {hookText}
          </div>
        </div>
      )}
      <div className="cca-script-flow">
        {stages.map((stage, i) => (
          <div key={i}>
            <div className="cca-script-stage">
              <div className="cca-script-stage-head">
                <span className="cca-script-stage-name">{stage.stage}</span>
                {stage.start_ms != null && stage.end_ms != null && (
                  <span className="cca-script-stage-time">
                    {formatClockRange(stage.start_ms, stage.end_ms)}
                  </span>
                )}
              </div>
              {stage.quote && <div className="cca-script-stage-quote">“{stage.quote}”</div>}
              <div className="cca-script-stage-row">
                <b>{t('competitiveAnalysis:script.function')}</b>
                {stage.function}
              </div>
              {stage.expression && (
                <div className="cca-script-stage-row">
                  <b>{t('competitiveAnalysis:script.expression')}</b>
                  {stage.expression}
                </div>
              )}
              {stage.why_effective && (
                <div className="cca-script-stage-row">
                  <b>{t('competitiveAnalysis:script.whyEffective')}</b>
                  {stage.why_effective}
                </div>
              )}
            </div>
            {i < stages.length - 1 && <div className="cca-script-arrow">↓</div>}
          </div>
        ))}
      </div>
    </div>
  )
}

function VisualTab({ detail }: { detail: ReferenceContentDetail }) {
  const { t } = useTranslation(['competitiveAnalysis'])
  const analysis = detail.analysis
  const media = detail.media_analysis
  const mediaVisible = isMediaLayerVisible(media)
  const objective = buildObjectiveVisualFacts(detail)
  const aiNotes = buildAiVisualNotes(analysis)

  if (!detail.capture || detail.capture.status !== 'succeeded') {
    const capturePending = capturePendingTextI18n(detail, t)
    return (
      <div className="cca-tab-body">
        <PendingBanner text={capturePending || t('competitiveAnalysis:pending.visualFetching')} />
      </div>
    )
  }

  if (!media || media.status === 'PENDING' || media.status === 'RUNNING') {
    return (
      <div className="cca-tab-body">
        <PendingBanner text={t('competitiveAnalysis:pending.analyzing')} />
      </div>
    )
  }

  if (media.status === 'FAILED' && !mediaVisible) {
    return (
      <div className="cca-tab-body">
        <div className="cca-tab-failed">{media.error_message || t('competitiveAnalysis:summary.analysisFailed')}</div>
      </div>
    )
  }

  const hasObjective =
    objective.motionLabels.length > 0 || objective.ocrTexts.length > 0 || objective.imagePageCount > 0 || (detail.shots?.length ?? 0) > 0

  return (
    <div className="cca-tab-body">
      <div className="cca-tab-section">
        <div className="cca-tab-section-title">{t('competitiveAnalysis:visual.objectiveTitle')}</div>
        {!hasObjective ? (
          <Empty description={t('competitiveAnalysis:visual.objectiveEmpty')} />
        ) : (
          <>
            {objective.motionLabels.length > 0 && (
              <div className="cca-summary-text" style={{ marginBottom: 12 }}>
                {t('competitiveAnalysis:visual.motionRhythm', { labels: objective.motionLabels.join(' · ') })}
              </div>
            )}
            {objective.ocrTexts.length > 0 && (
              <div style={{ marginBottom: 12 }}>
                <div className="cca-overview-label" style={{ marginBottom: 6 }}>
                  {t('competitiveAnalysis:visual.ocrTitle')}
                </div>
                <ul className="cca-insight-list">
                  {objective.ocrTexts.map((ocrText, i) => (
                    <li key={i}>{ocrText}</li>
                  ))}
                </ul>
              </div>
            )}
            {(detail.shots?.length ?? 0) > 0 && (
              <div className="cca-summary-text">
                {t('competitiveAnalysis:visual.shotsRecognized', { count: detail.shots.length })}
              </div>
            )}
            {objective.imagePageCount > 0 && (
              <div className="cca-summary-text">
                {t('competitiveAnalysis:visual.imagePagesCount', { count: objective.imagePageCount })}
              </div>
            )}
          </>
        )}
      </div>

      <div className="cca-tab-section">
        <div className="cca-tab-section-title">{t('competitiveAnalysis:visual.aiTitle')}</div>
        {analysis?.status === 'succeeded' ? (
          aiNotes.length > 0 ? (
            <ul className="cca-insight-list">
              {aiNotes.map((n, i) => (
                <li key={i}>{n}</li>
              ))}
            </ul>
          ) : (
            <Empty description={t('competitiveAnalysis:visual.aiEmpty')} />
          )
        ) : analysis?.status === 'failed' ? (
          <div className="cca-tab-failed">{analysis.error_message || t('competitiveAnalysis:visual.aiFailed')}</div>
        ) : (
          <PendingBanner text={t('competitiveAnalysis:pending.aiDeepAnalyzing')} />
        )}
      </div>
    </div>
  )
}

function InsightsTab({ detail }: { detail: ReferenceContentDetail }) {
  const { t } = useTranslation(['competitiveAnalysis'])
  const analysis = detail.analysis
  if (!analysis || analysis.status !== 'succeeded') {
    return (
      <div className="cca-tab-body">
        {analysis?.status === 'failed' ? (
          <div className="cca-tab-failed">{analysis.error_message || t('competitiveAnalysis:visual.aiFailed')}</div>
        ) : (
          <PendingBanner text={aiPendingCopyI18n(detail, t)} />
        )}
      </div>
    )
  }

  const sections = buildInsightSectionsI18n(analysis, t)
  if (sections.length === 0) {
    return (
      <div className="cca-tab-body">
        <Empty description={t('competitiveAnalysis:insights.empty')} />
      </div>
    )
  }

  return (
    <div className="cca-tab-body">
      <div className="cca-insight-columns">
        {sections.map((section) => (
          <div className="cca-tab-section" key={section.titleKey}>
            <div className="cca-tab-section-title">{section.title}</div>
            {section.body ? <div className="cca-summary-text" style={{ whiteSpace: 'pre-wrap' }}>{section.body}</div> : null}
            {section.items && section.items.length > 0 ? (
              <ul className="cca-insight-list">
                {section.items.map((item, i) => (
                  <li key={i}>{item}</li>
                ))}
              </ul>
            ) : null}
          </div>
        ))}
      </div>
    </div>
  )
}

function ReferenceCoverThumb({
  coverUrl,
  iconStyle,
}: {
  coverUrl: string | null
  iconStyle?: CSSProperties
}) {
  const [failed, setFailed] = useState(false)

  useEffect(() => {
    setFailed(false)
  }, [coverUrl])

  if (!coverUrl || failed) {
    return <FileImageOutlined style={iconStyle} />
  }

  return <img src={coverUrl} alt="" loading="lazy" onError={() => setFailed(true)} />
}
